"use client"

import React, { useMemo } from "react"

import { Button, cn } from "@coval/ui"
import { PanelLeftClose, PanelLeftOpen } from "lucide-react"

import { usePageShortcuts } from "@/hooks/usePageShortcuts"

const SIDEBAR_TOGGLE_SHORTCUT_KEY = "["

/**
 * `[` hides and restores the docked sidebar from anywhere outside a text
 * field. Registered once by the shell rather than by each toggle, since the
 * toggle can be mounted in more than one place at a time.
 */
export const useSidebarToggleShortcut = (
  onToggle: () => void,
  enabled: boolean,
) => {
  const shortcuts = useMemo(
    () => [
      {
        action: onToggle,
        command: {
          kind: "toggle" as const,
          scope: "global" as const,
          keywords: ["sidebar", "navigation", "collapse", "expand"],
        },
        key: SIDEBAR_TOGGLE_SHORTCUT_KEY,
        label: "Toggle sidebar",
      },
    ],
    [onToggle],
  )
  usePageShortcuts(shortcuts, enabled)
}

interface SidebarNavCollapseToggleProps {
  isCollapsed: boolean
  onToggle: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Placement classes from the slot the button sits in. */
  className?: string
  /** Hover handlers that open the hidden sidebar as a peek. */
  peekTriggerProps?: Pick<
    React.HTMLAttributes<HTMLButtonElement>,
    "onPointerEnter" | "onPointerLeave"
  >
}

/**
 * Hides and restores the docked primary navigation. Docked, it leads the
 * workspace selector at the top of the panel; hidden, it holds the header's
 * leading corner — the same spot — where hovering it peeks the sidebar.
 */
export const SidebarNavCollapseToggle = ({
  isCollapsed,
  onToggle,
  className,
  peekTriggerProps,
}: SidebarNavCollapseToggleProps) => {
  const label = isCollapsed ? "Show sidebar" : "Hide sidebar"

  return (
    <Button
      variant="secondary"
      icon
      tooltip={`${label} ( ${SIDEBAR_TOGGLE_SHORTCUT_KEY} )`}
      // Beside the button, so the tip never covers the peek opening below it.
      tooltipProps={{ side: "right" }}
      aria-label={label}
      aria-expanded={!isCollapsed}
      aria-keyshortcuts={SIDEBAR_TOGGLE_SHORTCUT_KEY}
      data-testid="sidebar-collapse-toggle"
      className={cn("shrink-0", className)}
      {...peekTriggerProps}
      onClick={onToggle}
    >
      {isCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
    </Button>
  )
}
